import React, { useEffect, useContext, useRef } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import PropTypes from 'prop-types';
import { ProviderContext, DropdownContext } from './Context';

const StyledDropdown = styled('div', {
  dynamic: ({ position }) => {
    return {
      position,
    };
  },
});
export const Dropdown = ({
  children,
  model,
  position,
  className,
  onChange,
  ...props
}) => {
  const dropdownRef = useRef();
  const { dropdownState, setDropdownState, ...values } =
    useContext(ProviderContext);

  if (!dropdownState) {
    throw new Error('<Dropdown> needs to be wrapped in a <Dropdown.Provider>');
  }

  let triggerElement;
  const isActive = dropdownState.model === model;

  const openDropdown = () => {
    setDropdownState((prevState) => {
      return prevState.model !== model ? { model } : prevState;
    });
  };

  const closeDropdown = () => {
    setDropdownState((prevState) => {
      if (triggerElement) {
        triggerElement.focus();
      }

      return prevState.model === model ? {} : prevState;
    });
  };

  const handleClickOutside = (e) => {
    if (!dropdownRef.current.contains(e.target)) {
      setDropdownState({});
    }
  };

  const handleKeyDown = (e) => {
    if (isActive) {
      if (e.key === 'Escape') {
        setDropdownState({});

        if (triggerElement) {
          triggerElement.focus();
        }
      }

      if (e.key === 'Tab') {
        setTimeout(() => {
          if (!dropdownRef.current.contains(document.activeElement)) {
            setDropdownState({});
          }
        });
      }
    }
  };

  useEffect(() => {
    triggerElement = document.activeElement;

    if (isActive) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    if (onChange) {
      onChange(isActive);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isActive]);

  return (
    <DropdownContext.Provider
      {...props}
      value={{
        ...values,
        dropdownState,
        setDropdownState,
        model,
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <StyledDropdown
        ref={dropdownRef}
        onKeyDown={handleKeyDown}
        className={className}
        position={position}
      >
        {children({ isActive, openDropdown, closeDropdown })}
      </StyledDropdown>
    </DropdownContext.Provider>
  );
};

Dropdown.propTypes = {
  children: PropTypes.func.isRequired,
  model: PropTypes.string.isRequired,
  className: PropTypes.string,
  position: PropTypes.string,
  onChange: PropTypes.func,
};

Dropdown.defaultProps = {
  className: null,
  position: 'relative',
  onChange: null,
};
